import { RSAA } from 'redux-api-middleware';
import constants from '../../constants';
import { CheckInActionType, Sale, Campaign, ApiResponse } from './types';

// Action Types
export const INIT_LOADING_START: CheckInActionType = 'INIT_LOADING_START';
export const INIT_LOADING_SUCCESS: CheckInActionType = 'INIT_LOADING_SUCCESS';
export const INIT_LOADING_FAILURE: CheckInActionType = 'INIT_LOADING_FAILURE';

export const SYNC_START: CheckInActionType = 'SYNC_START';
export const SYNC_SUCCESS: CheckInActionType = 'SYNC_SUCCESS';
export const SYNC_FAILURE: CheckInActionType = 'SYNC_FAILURE';

export const SEARCH_TERM_CHANGE: CheckInActionType = 'SEARCH_TERM_CHANGE';
export const SEARCH_RESULTS_UPDATE: CheckInActionType = 'SEARCH_RESULTS_UPDATE';

export const CHECK_IN_START: CheckInActionType = 'CHECK_IN_START';
export const CHECK_IN_SUCCESS: CheckInActionType = 'CHECK_IN_SUCCESS';
export const CHECK_IN_FAILURE: CheckInActionType = 'CHECK_IN_FAILURE';

export const CHECK_IN_INDIVIDUAL_START: CheckInActionType = 'CHECK_IN_INDIVIDUAL_START';
export const CHECK_IN_INDIVIDUAL_SUCCESS: CheckInActionType = 'CHECK_IN_INDIVIDUAL_SUCCESS';
export const CHECK_IN_INDIVIDUAL_FAILURE: CheckInActionType = 'CHECK_IN_INDIVIDUAL_FAILURE';

export const DEBUG_LOG_ADD: CheckInActionType = 'DEBUG_LOG_ADD';
export const ERROR_SET: CheckInActionType = 'ERROR_SET';
export const ERROR_CLEAR: CheckInActionType = 'ERROR_CLEAR';

// Helper function to get common headers
const getHeaders = (sessionKey: string) => ({
  'ApiKey': constants.apikey || '',
  'Content-Type': 'application/json',
  'Authorization': sessionKey ? `bearer ${sessionKey}` : ''
});

// Action Creators

/**
 * Initialize check-in page - fetch campaign and sales data
 */
export const initializeCheckIn = (campaignId: string) => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const sessionKey = state.session?.key;

    dispatch(addDebugLog('info', 'Initializing check-in page...'));
    dispatch({ type: INIT_LOADING_START });

    try {
      // First, fetch campaign details to verify it's live
      dispatch(addDebugLog('info', `Fetching campaign ${campaignId}...`));
      const campaignResponse = await fetch(
        `${constants.baseApiHost}/api/v2/page/campaign/${campaignId}`,
        {
          method: 'GET',
          headers: getHeaders(sessionKey)
        }
      );

      if (!campaignResponse.ok) {
        throw new Error(`Campaign fetch failed: ${campaignResponse.status} ${campaignResponse.statusText}`);
      }

      const campaignData: ApiResponse<Campaign> = await campaignResponse.json();

      if (!campaignData.success || !campaignData.data) {
        throw new Error('Campaign not found or invalid response');
      }

      const campaign = campaignData.data;

      if (campaign.status !== 'live') {
        throw new Error(`Campaign status is "${campaign.status}", expected "live"`);
      }

      dispatch(addDebugLog('success', `Campaign "${campaign.name}" verified as live`));

      // Fetch all sales/entries for this campaign
      dispatch(addDebugLog('info', 'Fetching sales data...'));
      const salesResponse = await fetch(
        `${constants.baseApiHost}/api/v2/sale/campaign/${campaignId}/check-in`,
        {
          method: 'GET',
          headers: getHeaders(sessionKey)
        }
      );

      if (!salesResponse.ok) {
        throw new Error(`Sales fetch failed: ${salesResponse.status} ${salesResponse.statusText}`);
      }

      const salesData: ApiResponse<Sale[]> = await salesResponse.json();

      if (!salesData.success) {
        throw new Error('Failed to fetch sales data');
      }

      const sales = salesData.data || [];
      const dataSize = JSON.stringify(sales).length;

      dispatch(addDebugLog('success',
        `Received ${sales.length} sales with ${sales.reduce((total, sale) => total + sale.details.length, 0)} entries (${Math.round(dataSize / 1024)}KB)`
      ));

      dispatch({
        type: INIT_LOADING_SUCCESS,
        payload: {
          campaign,
          sales
        }
      });

      // Start background sync timer
      dispatch(startBackgroundSync(campaignId));

    } catch (error: any) {
      dispatch(addDebugLog('error', `Initialization failed: ${error.message}`));
      dispatch({
        type: INIT_LOADING_FAILURE,
        payload: { error: error.message }
      });
    }
  };
};

/**
 * Start background sync every 30 seconds
 */
export const startBackgroundSync = (campaignId: string) => {
  return (dispatch: any, getState: any) => {
    const syncInterval = setInterval(() => {
      const state = getState();
      const lastSyncTime = state.checkIn?.lastSyncTime;

      if (lastSyncTime) {
        dispatch(syncUpdates(campaignId, lastSyncTime));
      }
    }, 90000); // 30 seconds

    // Store interval ID in window for cleanup
    (window as any).checkInSyncInterval = syncInterval;
  };
};

/**
 * Sync incremental updates from the server
 */
export const syncUpdates = (campaignId: string, lastSyncTime: number) => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const sessionKey = state.session?.key;

    dispatch({ type: SYNC_START });

    try {
      const timestamp = new Date(lastSyncTime).toISOString();
      const response = await fetch(
        `${constants.baseApiHost}/api/v2/sale/campaign/${campaignId}/check-in?updatedAfter=${timestamp}`,
        {
          method: 'GET',
          headers: getHeaders(sessionKey)
        }
      );

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status} ${response.statusText}`);
      }

      const data: ApiResponse<Sale[]> = await response.json();

      if (!data.success) {
        throw new Error('Sync request failed');
      }

      const updatedSales = data.data || [];

      if (updatedSales.length > 0) {
        dispatch(addDebugLog('info', `Background sync: ${updatedSales.length} sales updated`));
        dispatch({
          type: SYNC_SUCCESS,
          payload: { updatedSales }
        });
      } else {
        dispatch({
          type: SYNC_SUCCESS,
          payload: { updatedSales: [] }
        });
      }

    } catch (error: any) {
      dispatch(addDebugLog('warning', `Background sync failed: ${error.message}`));
      dispatch({
        type: SYNC_FAILURE,
        payload: { error: error.message }
      });
    }
  };
};

/**
 * Check in entire sale (all entries)
 */
export const checkInSale = (saleId: string) => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const sessionKey = state.session?.key;

    dispatch({
      type: CHECK_IN_START,
      payload: { saleId }
    });

    dispatch(addDebugLog('info', `Checking in sale ${saleId}...`));

    try {
      const response = await fetch(
        `${constants.baseApiHost}/api/v2/sale/${saleId}/check-in`,
        {
          method: 'POST',
          headers: getHeaders(sessionKey)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.[0]?.error || `Check-in failed: ${response.status}`);
      }

      const data: ApiResponse<Sale> = await response.json();

      if (!data.success || !data.data) {
        throw new Error(data.error?.[0]?.error || 'Check-in failed');
      }

      dispatch(addDebugLog('success', `Sale ${saleId} checked in successfully - using API response data`));
      dispatch({
        type: CHECK_IN_SUCCESS,
        payload: {
          saleId,
          updatedSale: data.data
        }
      });

    } catch (error: any) {
      dispatch(addDebugLog('error', `Check-in failed for sale ${saleId}: ${error.message}`));
      dispatch({
        type: CHECK_IN_FAILURE,
        payload: {
          saleId,
          error: error.message
        }
      });
    }
  };
};

/**
 * Check in individual entry
 */
export const checkInIndividual = (saleId: string, saleDetailId: string) => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const sessionKey = state.session?.key;

    // For the entryKey, we still use index-based format for state management
    // But we pass the actual saleDetailId to the API
    const sale = state.checkIn?.sales?.[saleId];
    const entryIndex = sale?.details?.findIndex((entry: any) =>
      entry.id && entry.id.toString() === saleDetailId
    ) ?? -1;

    const entryKey = entryIndex >= 0 ? `${saleId}-${entryIndex}` : `${saleId}-${saleDetailId}`;

    dispatch({
      type: CHECK_IN_INDIVIDUAL_START,
      payload: { saleId, saleDetailId, entryKey }
    });

    dispatch(addDebugLog('info', `Checking in individual entry - saleId: ${saleId}, saleDetailId: ${saleDetailId}, entryKey: ${entryKey}...`));

    try {
      const response = await fetch(
        `${constants.baseApiHost}/api/sale/${saleId}/check-in/${saleDetailId}`,
        {
          method: 'POST',
          headers: getHeaders(sessionKey)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.[0]?.error || `Individual check-in failed: ${response.status}`);
      }

      const data: ApiResponse<Sale> = await response.json();

      if (!data.success || !data.data) {
        throw new Error(data.error?.[0]?.error || 'Individual check-in failed');
      }

      dispatch(addDebugLog('success', `Entry checked in successfully - using API response data - saleId: ${saleId}, saleDetailId: ${saleDetailId}, entryKey: ${entryKey}`));
      dispatch({
        type: CHECK_IN_INDIVIDUAL_SUCCESS,
        payload: {
          saleId,
          saleDetailId,
          entryKey,
          updatedSale: data.data
        }
      });

    } catch (error: any) {
      dispatch(addDebugLog('error', `Individual check-in failed - saleId: ${saleId}, saleDetailId: ${saleDetailId}, entryKey: ${entryKey}: ${error.message}`));
      dispatch({
        type: CHECK_IN_INDIVIDUAL_FAILURE,
        payload: {
          saleId,
          saleDetailId,
          entryKey,
          error: error.message
        }
      });
    }
  };
};

/**
 * Update search term and trigger search
 */
export const updateSearchTerm = (searchTerm: string) => {
  return (dispatch: any, getState: any) => {
    dispatch({
      type: SEARCH_TERM_CHANGE,
      payload: { searchTerm }
    });

    // Debounced search will be handled in the component
  };
};

/**
 * Update search results
 */
export const updateSearchResults = (results: any[]) => {
  return {
    type: SEARCH_RESULTS_UPDATE,
    payload: { results }
  };
};

/**
 * Add debug log entry
 */
export const addDebugLog = (type: 'info' | 'error' | 'success' | 'warning', message: string) => {
  return {
    type: DEBUG_LOG_ADD,
    payload: {
      log: {
        timestamp: new Date().toLocaleTimeString(),
        message,
        type
      }
    }
  };
};

/**
 * Set error
 */
export const setError = (key: string, message: string) => {
  return {
    type: ERROR_SET,
    payload: { key, message }
  };
};

/**
 * Clear error
 */
export const clearError = (key: string) => {
  return {
    type: ERROR_CLEAR,
    payload: { key }
  };
};

/**
 * Cleanup function for when leaving the page
 */
export const cleanupCheckIn = () => {
  return (dispatch: any) => {
    // Clear background sync interval
    if ((window as any).checkInSyncInterval) {
      clearInterval((window as any).checkInSyncInterval);
      delete (window as any).checkInSyncInterval;
    }

    dispatch(addDebugLog('info', 'Check-in page cleanup completed'));
  };
};
